/**
 * ReclaimGolfHub — the golf upsell surface inside Reclaim.
 * Wraps every golf audio + create-flow + coaching structure into
 * one paid Reclaim product. NOT part of Soulumo (which stays flat:
 * just audio installations, browsable individually).  Mar 8 2026.
 *
 * Sources pulled together:
 *   1. Signature Blueprints tagged category="golf"
 *      (Full Round, Chipping & Short Game, Overnight · Golf-Ready)
 *   2. Daily Edge tracks     — /api/golf/tracks/daily-edge
 *      (Pre Tee Primer, Poor Shot Reset, Flow State Reset)
 *   3. Deeper Work tracks    — /api/golf/tracks/deeper-work
 *      (Alignment, Identity Loop, Putting, Short Game, Long Game,
 *       Competition)
 *   4. Performance Focus     — from data/performance_data.py
 *      (Driving, Irons, Putting, Pressure, Bunker, Course Mgmt)
 *
 * Sam Horsfield content is filtered out by the backend blueprint
 * endpoint (visibility="bespoke" + visible_to_user_id gate), so
 * nothing bespoke ever leaks into this catalogue.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, Play, Moon, Sparkles, Target, Wind, Sun, Flag, X } from "lucide-react";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";
import InstallOrb from "../../components/InstallOrb";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () =>
  typeof window !== "undefined" ? localStorage.getItem("reclaim_token") : null;
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const TOK = {
  bg: "radial-gradient(ellipse at 30% 12%, #1e3a24 0%, #0f2318 38%, #061309 72%, #030704 100%)",
  cream: "#f0e8c9",
  creamDim: "rgba(240,232,201,0.75)",
  creamMist: "rgba(240,232,201,0.44)",
  gold: "#d4c07a",
  goldSoft: "rgba(212,192,122,0.86)",
  goldMist: "rgba(212,192,122,0.24)",
  greenAccent: "#7ab486",
  ink: "#0f2318",
};

// Static reference for the 6 Performance focus areas so we can
// present them beside the API-driven tracks in one shelf. Ids
// map to the private-suite performance module.
const PERFORMANCE_FOCUS = [
  { id: "driving",         label: "Driving",         blurb: "Power through stillness." },
  { id: "irons",           label: "Irons",           blurb: "Precision is my language." },
  { id: "putting",         label: "Putting",         blurb: "Stillness before the stroke." },
  { id: "pressure",        label: "Under Pressure",  blurb: "I rise when it matters." },
  { id: "bunker",          label: "Bunker Mastery",  blurb: "I love the sand." },
  { id: "course_mgmt",     label: "Course Management", blurb: "Think like a strategist." },
];

// Mar 8 2026 · Playing Free trilogy — Michael's voice, pre-rendered
// full installations. Published to Soulumo via public share slugs
// so any visitor can play them without cloning or re-render.
const PLAYING_FREE = [
  {
    id: "playing_free_foundation",
    label: "Playing Free · Foundation",
    blurb: "The ground beneath the round. Twelve minutes to arrive.",
    duration: 6 * 60,
    slug: "j-fubdvqta5t",
  },
  {
    id: "playing_free_trust",
    label: "Playing Free · Trust",
    blurb: "Twelve minutes settling into the swing that is already yours.",
    duration: 8 * 60,
    slug: "j-6dam35uyuh",
  },
  {
    id: "playing_free_full",
    label: "Playing Free",
    blurb: "The full fifteen-minute installation. Freedom over the ball.",
    duration: 15 * 60,
    slug: "j-yjssgz4b0",
  },
];

const fmtSec = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return sec ? `${m}m ${sec}s` : `${m}m`;
  return `${sec}s`;
};

export default function ReclaimGolfHub() {
  const nav = useNavigate();
  const location = useLocation();
  // Oct 3 2026 · Michael — Golf is a Reclaim upsell product.
  //   /reclaim/golf  → use Reclaim's native chrome (back arrow + the
  //                    global BottomNav already renders here).
  //   /soulumo/golf  → keep the Soulumo branded header/footer so it
  //                    reads as a soft upsell inside Soulumo.
  const isReclaimHost = location.pathname.startsWith("/reclaim");

  const [signatures, setSignatures] = useState([]);
  const [dailyEdge, setDailyEdge] = useState([]);
  const [deeperWork, setDeeperWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [upsellDismissed, setUpsellDismissed] = useState(() => {
    try { return localStorage.getItem("golf_upsell_dismissed") === "1"; }
    catch (_) { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sigsRes, dailyRes, deepRes, meRes] = await Promise.all([
          fetch(`${API}/api/audio-journeys/blueprints`, { headers: authHeaders() }).then((r) => r.json()).catch(() => ({ items: [] })),
          fetch(`${API}/api/golf/tracks/daily-edge`).then((r) => r.json()).catch(() => ({ tracks: [] })),
          fetch(`${API}/api/golf/tracks/deeper-work`).then((r) => r.json()).catch(() => ({ tracks: [] })),
          fetch(`${API}/api/auth/me`, { headers: authHeaders() })
            .then((r) => r.ok ? r.json() : null)
            .catch(() => null),
        ]);
        if (cancelled) return;
        const sigs = (sigsRes.items || []).filter((b) => b.category === "golf");
        setSignatures(sigs);
        setDailyEdge(dailyRes.tracks || []);
        setDeeperWork(deepRes.tracks || []);
        setProfile(meRes);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismissUpsell = () => {
    setUpsellDismissed(true);
    try { localStorage.setItem("golf_upsell_dismissed", "1"); } catch (_) { /* localStorage blocked */ }
  };

  // Oct 3 2026 · Subtle lifecycle upsell. Michael wants the Golf
  // Program nudge to arrive AFTER a tester has settled in — about a
  // week — so it never reads as a hard paywall. Rules:
  //   · logged-in users only
  //   · 7+ days since registration
  //   · not an admin (they already see everything)
  //   · not already dismissed on this device
  //   · membership_tier is not already a paid tier that includes golf
  const now = Date.now();
  const createdMs = profile?.created_at ? Date.parse(profile.created_at) : NaN;
  const daysSinceSignup = Number.isFinite(createdMs)
    ? Math.max(0, Math.floor((now - createdMs) / 86400000))
    : null;
  const paidTiers = ["premium", "inner_circle", "admin"];
  const alreadyIncluded = paidTiers.includes(profile?.membership_tier || "")
    || profile?.role === "admin";
  const showUpsell = Boolean(
    profile
    && !upsellDismissed
    && !alreadyIncluded
    && daysSinceSignup !== null
    && daysSinceSignup >= 7
  );

  return (
    <div
      className={`min-h-screen flex flex-col overflow-x-hidden ${isReclaimHost ? "pb-24" : ""}`}
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="reclaim-golf-hub"
    >
      {isReclaimHost ? (
        <ReclaimGolfTopBar />
      ) : (
        <SoulumoHeader />
      )}

      <main className="relative z-10 flex-1 px-5 sm:px-8 pb-16">
        <div className="max-w-3xl mx-auto">
          {showUpsell && (
            <GolfUpsellBanner
              daysSinceSignup={daysSinceSignup}
              onDismiss={dismissUpsell}
            />
          )}

          {/* Hero */}
          <div className="text-center mt-8 mb-10">
            <div style={{ width: 110, height: 110, margin: "0 auto" }}>
              <InstallOrb state="resting" size={110} />
            </div>
            <p
              className="mt-5 text-[0.62rem] uppercase tracking-[0.4em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              · Reclaim · Golf ·
            </p>
            <h1
              className="mt-4 font-light leading-tight"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(2.1rem, 6vw, 2.9rem)",
                color: TOK.cream,
                letterSpacing: "-0.01em",
              }}
            >
              For the round, the range, and the night before.
            </h1>
            <p
              className="mt-4 italic mx-auto max-w-lg leading-relaxed"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.08rem" }}
            >
              Every part of the mental game — from the first tee to
              the last putt to the sleep before the tournament.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16" style={{ color: TOK.creamMist }}>
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : (
            <>
              <Shelf icon={Sparkles} title="Signature Installations" subtitle="Full-length installations. Yours to keep.">
                {signatures.map((b) => (
                  <BlueprintCard
                    key={b.id}
                    label={b.label}
                    blurb={b.blurb}
                    meta={`${b.duration_minutes} min · ${b.binaural_stages?.[0] || ""} bed`}
                    accent={b.accent || TOK.greenAccent}
                    onClick={() => nav(`/signature-installations/${b.id}`)}
                    testid={`golf-sig-${b.id}`}
                  />
                ))}
              </Shelf>

              {/* Playing Free — Michael's voice, in full. Highlighted
                  above the shorter Deep Work / Daily Edge tracks so
                  it reads as the flagship shelf of the launch. */}
              <Shelf icon={Moon} title="Playing Free" subtitle="Michael's voice. Three installations from six to fifteen minutes.">
                {PLAYING_FREE.map((t) => (
                  <BlueprintCard
                    key={t.id}
                    label={t.label}
                    blurb={t.blurb}
                    meta={`${Math.round(t.duration / 60)} min · Michael's voice`}
                    accent={TOK.gold}
                    onClick={() => nav(`/share/audio/${t.slug}`)}
                    testid={`golf-playing-free-${t.id}`}
                  />
                ))}
              </Shelf>

              <Shelf icon={Wind} title="Daily Edge" subtitle="Short, in-the-moment resets during your round.">
                {dailyEdge.map((t) => (
                  <BlueprintCard
                    key={t.id}
                    label={t.title}
                    blurb={t.subtitle}
                    meta={fmtSec(t.duration)}
                    accent={TOK.greenAccent}
                    onClick={() => nav(`/golf/deep-work/${t.id}`)}
                    testid={`golf-daily-${t.id}`}
                  />
                ))}
              </Shelf>

              <Shelf icon={Target} title="Deeper Work" subtitle="Identity installations for the parts of your game that matter most.">
                {deeperWork.map((t) => (
                  <BlueprintCard
                    key={t.id}
                    label={t.title}
                    blurb={t.subtitle}
                    meta={fmtSec(t.duration)}
                    accent={TOK.gold}
                    onClick={() => nav(`/golf/deep-work/${t.id}`)}
                    testid={`golf-deep-${t.id}`}
                  />
                ))}
              </Shelf>

              <Shelf icon={Sun} title="Performance Focus" subtitle="Build Your Own installations tuned to one part of your game.">
                {PERFORMANCE_FOCUS.map((f) => (
                  <BlueprintCard
                    key={f.id}
                    label={f.label}
                    blurb={f.blurb}
                    meta="Create · Personalised"
                    accent={TOK.greenAccent}
                    onClick={() => nav(`/my-installations?theme=golf&focus=${f.id}`)}
                    testid={`golf-focus-${f.id}`}
                  />
                ))}
              </Shelf>
            </>
          )}
        </div>
      </main>

      {isReclaimHost ? null : <SoulumoFooter />}
    </div>
  );
}

function Shelf({ icon: Icon, title, subtitle, children }) {
  const hasChildren = React.Children.count(children) > 0;
  if (!hasChildren) return null;
  return (
    <section className="mt-10">
      <div className="flex items-baseline gap-3 mb-4">
        <Icon size={14} color={TOK.goldSoft} />
        <div>
          <h2
            className="font-light leading-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.6rem", color: TOK.cream }}
          >
            {title}
          </h2>
          <p
            className="italic"
            style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.94rem" }}
          >
            {subtitle}
          </p>
        </div>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</ul>
    </section>
  );
}

function BlueprintCard({ label, blurb, meta, accent, onClick, testid }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={testid}
        className="w-full text-left rounded-2xl p-5 transition-all"
        style={{
          background: "linear-gradient(135deg, rgba(212,192,122,0.08) 0%, rgba(212,192,122,0.02) 100%)",
          border: `1px solid ${accent}55`,
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 rounded-full w-11 h-11 flex items-center justify-center"
            style={{
              background: `${accent}22`,
              border: `1px solid ${accent}66`,
            }}
          >
            <Play size={14} color={accent} fill={accent} />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-lg leading-tight truncate"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}
            >
              {label}
            </h3>
            <p
              className="mt-1 italic leading-snug"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.94rem" }}
            >
              {blurb}
            </p>
            <div
              className="mt-3 text-[0.56rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
            >
              {meta}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// ReclaimGolfTopBar — Oct 2026
// A slim, Reclaim-native top bar used only on /reclaim/golf. Keeps
// the green golf background but replaces the Soulumo header with a
// familiar Reclaim pattern (back arrow + section crumb). The global
// BottomNav already renders on this route via NarrowChromeGate.
// ────────────────────────────────────────────────────────────────────
function ReclaimGolfTopBar() {
  // Oct 2026 · Reclaim's native floating HOME button already covers
  // "take me back" on this route (rendered by NarrowChromeGate).
  // We only add a slim crumb so the user always knows they're inside
  // Reclaim · Golf; no second back button to compete with HOME.
  return (
    <header
      className="sticky top-0 z-20 backdrop-blur-md"
      style={{
        background: "linear-gradient(180deg, rgba(6,19,9,0.78) 0%, rgba(6,19,9,0.0) 100%)",
        borderBottom: `1px solid ${TOK.goldMist}`,
      }}
      data-testid="reclaim-golf-topbar"
    >
      <div className="max-w-3xl mx-auto flex items-center justify-center px-5 py-3">
        <div
          className="flex items-center gap-2 uppercase"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.6rem",
            letterSpacing: "0.32em",
          }}
          data-testid="reclaim-golf-crumb"
        >
          <Flag size={11} />
          Reclaim · Golf
        </div>
      </div>
    </header>
  );
}

// ────────────────────────────────────────────────────────────────────
// GolfUpsellBanner — Oct 2026
// Soft, dismissable lifecycle upsell shown AFTER a Reclaim user has
// been active for 7+ days. Not a hard paywall — the hub remains
// fully browsable; this is a warm invitation to join the paid Golf
// Program once pricing lands.
// ────────────────────────────────────────────────────────────────────
function GolfUpsellBanner({ daysSinceSignup, onDismiss }) {
  const [showForm, setShowForm] = useState(false);
  return (
    <div
      data-testid="golf-upsell-banner"
      className="relative mt-6 rounded-2xl p-5 sm:p-6"
      style={{
        background: "linear-gradient(135deg, rgba(122,180,134,0.14) 0%, rgba(212,192,122,0.08) 100%)",
        border: `1px solid ${TOK.goldMist}`,
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        data-testid="golf-upsell-dismiss"
        className="absolute top-3 right-3 opacity-60 hover:opacity-100 transition"
        style={{ color: TOK.creamMist, minWidth: 32, minHeight: 32 }}
      >
        <X size={16} />
      </button>
      <div
        className="uppercase mb-2"
        style={{
          color: TOK.goldSoft,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.58rem",
          letterSpacing: "0.36em",
        }}
      >
        {daysSinceSignup != null ? `Day ${daysSinceSignup} · a quiet invitation` : "A quiet invitation"}
      </div>
      <h3
        className="font-light leading-tight"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.4rem, 4.6vw, 1.75rem)",
        }}
      >
        Ready to go deeper on your game?
      </h3>
      <p
        className="mt-2 italic max-w-md"
        style={{
          color: TOK.creamDim,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1rem",
          lineHeight: 1.55,
        }}
      >
        The Golf Program is Reclaim's dedicated mental&nbsp;game pathway —
        every signature installation, the full Deeper&nbsp;Work library,
        and personal coaching touchpoints. Opening to a small first cohort.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="golf-upsell-cta"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 transition"
          style={{
            background: TOK.gold,
            color: TOK.ink,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            minHeight: 40,
          }}
        >
          <Flag size={13} />
          Reserve my place
        </button>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="golf-upsell-not-now"
          className="rounded-full px-4 py-2 transition"
          style={{
            background: "transparent",
            border: `1px solid ${TOK.goldMist}`,
            color: TOK.creamDim,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            minHeight: 40,
          }}
        >
          Not right now
        </button>
      </div>
      {showForm && (
        <GolfWaitlistModal
          onClose={() => setShowForm(false)}
          onJoined={() => {
            setShowForm(false);
            onDismiss(); // once they've joined the list, no need to keep nudging
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// GolfWaitlistModal — Oct 2026
// A short, three-field opt-in that writes to /api/golf/waitlist and
// emails Michael. Sortable, replyable, no inbox-flood.
// ────────────────────────────────────────────────────────────────────
function GolfWaitlistModal({ onClose, onJoined }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [handicap, setHandicap] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  // Pre-fill from the logged-in profile if we have it.
  useEffect(() => {
    (async () => {
      try {
        const me = await fetch(`${API}/api/auth/me`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : null);
        if (me) {
          setName((n) => n || me.name || "");
          setEmail((e) => e || me.email || "");
        }
      } catch (_) { /* fine */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/golf/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          handicap: handicap.trim() || null,
          note: note.trim() || null,
          source: "golf_hub",
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      setSent(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    width: "100%",
    background: "rgba(0,0,0,0.3)",
    border: `1px solid ${TOK.goldMist}`,
    color: TOK.cream,
    borderRadius: 12,
    padding: "12px 14px",
    fontFamily: '"Manrope", sans-serif',
    fontSize: "0.92rem",
    minHeight: 44,
    outline: "none",
  };

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-5"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="golf-waitlist-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6"
        style={{
          background: TOK.ink,
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
          maxHeight: "92vh",
          overflowY: "auto",
        }}
      >
        {sent ? (
          <div className="text-center py-6" data-testid="golf-waitlist-sent">
            <div
              className="uppercase mb-2"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.32em",
              }}
            >
              You&apos;re on the list
            </div>
            <h3
              className="italic font-light"
              style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.7rem" }}
            >
              Thank you.
            </h3>
            <p
              className="mt-3 italic max-w-sm mx-auto"
              style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem", lineHeight: 1.55 }}
            >
              Michael opens the Golf Program in small cohorts. He&apos;ll be in
              touch personally when a place is yours.
            </p>
            <button
              type="button"
              onClick={onJoined}
              data-testid="golf-waitlist-close"
              className="mt-6 rounded-full px-6 py-2.5"
              style={{
                background: TOK.gold,
                color: TOK.ink,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.78rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Return to Golf
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="golf-waitlist-form">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div
                  className="uppercase"
                  style={{
                    color: TOK.goldSoft,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.58rem",
                    letterSpacing: "0.36em",
                  }}
                >
                  Reserve my place
                </div>
                <h3
                  className="mt-1 italic font-light"
                  style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.5rem" }}
                >
                  The Golf Program
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="golf-waitlist-close-x"
                className="opacity-60 hover:opacity-100"
                style={{ color: TOK.creamMist, minWidth: 36, minHeight: 36 }}
              >
                <X size={18} />
              </button>
            </div>

            <input
              type="text"
              required
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={fieldStyle}
              data-testid="golf-waitlist-name"
              autoComplete="name"
            />
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={fieldStyle}
              data-testid="golf-waitlist-email"
              autoComplete="email"
            />
            <input
              type="text"
              placeholder="Current handicap (optional)"
              value={handicap}
              onChange={(e) => setHandicap(e.target.value)}
              style={fieldStyle}
              data-testid="golf-waitlist-handicap"
              inputMode="decimal"
            />
            <textarea
              placeholder="Anything you want Michael to know? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              style={{ ...fieldStyle, minHeight: 92, resize: "vertical" }}
              data-testid="golf-waitlist-note"
            />
            {error && (
              <div className="text-sm" style={{ color: "#f0a0a0" }} data-testid="golf-waitlist-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="golf-waitlist-submit"
              className="w-full rounded-full py-3 disabled:opacity-60"
              style={{
                background: TOK.gold,
                color: TOK.ink,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.78rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                minHeight: 44,
              }}
            >
              {loading ? "Sending…" : "Reserve my place"}
            </button>
            <p
              className="text-center italic pt-1"
              style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.82rem" }}
            >
              Michael replies personally. No marketing lists.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

